import { useMemo } from 'react';
import { useCropMonitorings } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import type { CropMonitoring } from '/@/types/crop-monitoring';

export type LastCampaign = {
    /** Harvested yield of that campaign, in kg/ha. */
    averageYield: number;
    plantingDate: string | null;
    productName?: string;
    crop: CropMonitoring['crop'];
};

/** Most recent crop monitoring, from a list already sorted newest-first, that was actually harvested. */
const extractLastCampaign = (cropMonitorings: CropMonitoring[]): LastCampaign | undefined => {
    const harvested = cropMonitorings.find((cropMonitoring) => !!cropMonitoring.result?.averageYield);
    if (!harvested?.result) return undefined;
    return {
        averageYield: harvested.result.averageYield,
        plantingDate: harvested.plantingDate ?? null,
        productName: harvested.productName || harvested.cornCropMonitoring?.productName,
        crop: harvested.crop,
    };
};

/**
 * Last harvested corn campaign for a field — the most recent crop monitoring that
 * actually produced a harvest result. Fields with no monitoring history (or with
 * monitorings that were never harvested) simply resolve to `undefined`.
 */
export const useLastCampaign = (fieldId?: number): { lastCampaign?: LastCampaign; isLoading: boolean } => {
    const { data, isLoading } = useCropMonitorings({
        page: 1,
        pageSize: 20,
        fieldId: fieldId ? String(fieldId) : undefined,
        crop: 'corn',
        ordering: '-planting_date',
    });

    const lastCampaign = useMemo(() => {
        if (!fieldId) return undefined;
        return extractLastCampaign(data?.results || []);
    }, [data?.results, fieldId]);

    return { lastCampaign, isLoading };
};

/**
 * Last harvested campaign for a LIST of fields (any crop — corn, soy, etc.), resolved with a
 * single request scoped by node (mirrors the node/includeChildren/excludeTargetNode scoping used
 * to fetch the field list itself) — avoids firing one crop-monitoring request per row.
 */
export const useLastCampaignsBulk = ({
    node,
    includeChildren,
    excludeTargetNode,
}: {
    node?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
}): { lastCampaigns: Map<number, LastCampaign>; isLoading: boolean } => {
    const { data, isLoading } = useCropMonitorings({
        page: 1,
        pageSize: 10000,
        node,
        includeChildren,
        excludeTargetNode,
        ordering: '-planting_date',
    });

    const lastCampaigns = useMemo(() => {
        const byField = new Map<number, CropMonitoring[]>();
        for (const cropMonitoring of data?.results || []) {
            const fieldCropMonitorings = byField.get(cropMonitoring.field) || [];
            fieldCropMonitorings.push(cropMonitoring);
            byField.set(cropMonitoring.field, fieldCropMonitorings);
        }

        const result = new Map<number, LastCampaign>();
        for (const [fieldId, cropMonitorings] of byField) {
            const lastCampaign = extractLastCampaign(cropMonitorings);
            if (lastCampaign) result.set(fieldId, lastCampaign);
        }
        return result;
    }, [data?.results]);

    return { lastCampaigns, isLoading };
};
